import React from 'react';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Input } from 'components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'components/ui/select';
import {
  mergeVoicePresentation, previewVoicePresentation, readVoicePresentation,
  resetVoicePresentation, type VoicePresentation,
} from 'utils/voicePresentation';
import type { QuestionOptionEditable } from './types';

interface VoicePresentationEditorProps {
  metadata?: Record<string, unknown>;
  questionType: string;
  questionText: string;
  options: QuestionOptionEditable[];
  disabled: boolean;
  error: string | null;
  onChange: (metadata: Record<string, unknown>) => void;
}

export const VoicePresentationEditor: React.FC<VoicePresentationEditorProps> = ({
  metadata, questionType, questionText, options, disabled, error, onChange,
}) => {
  const voice = readVoicePresentation(metadata);
  const preview = previewVoicePresentation(metadata, questionType, questionText, options);
  const isChoice = questionType === 'multiple_choice' || questionType === 'multiple_select';
  const hasOther = options.some((option) => option.is_other);
  const update = (patch: Partial<VoicePresentation>) => onChange(mergeVoicePresentation(metadata, patch));

  return (
    <section aria-labelledby="voice-presentation-heading" className="space-y-3 rounded-3xl border border-border/40 bg-white/40 p-4">
      <h3 id="voice-presentation-heading" className="text-xs font-semibold uppercase text-muted-foreground">Voice presentation</h3>
      <p className="text-xs text-muted-foreground">
        Customize the spoken experience in modules that have Voice Mode enabled. Defaults use the existing question content.
      </p>
      <div className="space-y-1">
        <Label htmlFor="voice-spoken-prompt">Spoken prompt</Label>
        <Textarea id="voice-spoken-prompt" rows={2} value={voice.spoken_prompt} disabled={disabled}
          onChange={(event) => update({ spoken_prompt: event.target.value })}
          placeholder="Leave blank to use the question text" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="voice-guide-instructions">Guide instructions</Label>
        <Textarea id="voice-guide-instructions" rows={2} value={voice.guide_instructions} disabled={disabled}
          onChange={(event) => update({ guide_instructions: event.target.value })}
          aria-describedby="voice-guide-help" />
        <p id="voice-guide-help" className="text-xs text-muted-foreground">
          Direct presentation and follow-up only. Instructions cannot override answer validation, scoring, completion, or safety rules.
        </p>
      </div>
      {isChoice ? (
        <>
          <div className="space-y-1">
            <Label htmlFor="voice-option-presentation">Read options</Label>
            <Select value={voice.option_presentation} disabled={disabled}
              onValueChange={(value) => update({ option_presentation: value as VoicePresentation['option_presentation'] })}>
              <SelectTrigger id="voice-option-presentation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All options</SelectItem>
                <SelectItem value="examples">Some examples</SelectItem>
                <SelectItem value="none">Do not read options initially</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Learners can always ask to read all options. Quizzes and surveys require all choices and cannot widen the accepted answers.</p>
          </div>
          {voice.option_presentation === 'examples' ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Optional examples; leave empty to read the first three active option labels.</p>
              {(voice.examples ?? []).map((example, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Label htmlFor={`voice-example-${index}`} className="sr-only">Voice example {index + 1}</Label>
                  <Input id={`voice-example-${index}`} value={example} disabled={disabled}
                    onChange={(event) => update({ examples: (voice.examples ?? []).map((value, itemIndex) => itemIndex === index ? event.target.value : value) })} />
                  <Button type="button" variant="ghost" disabled={disabled}
                    aria-label={`Remove voice example ${index + 1}`}
                    onClick={() => update({ examples: voice.examples?.filter((_, itemIndex) => itemIndex !== index) })}>Remove</Button>
                </div>
              ))}
              <Button type="button" variant="secondary" disabled={disabled}
                onClick={() => update({ examples: [...(voice.examples ?? []), ''] })}>Add voice example</Button>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Checkbox id="voice-allow-other" checked={voice.allow_other_answers} disabled={disabled || !hasOther}
              onCheckedChange={(checked) => update({ allow_other_answers: checked === true })} />
            <Label htmlFor="voice-allow-other">Allow answers through the existing Other option</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            {hasOther ? 'Unmatched answers use Other and count as one selection under existing limits.' : 'Add an Other option to the question before enabling answers outside the listed choices.'}
          </p>
        </>
      ) : null}
      <div className="space-y-1">
        <Label htmlFor="voice-spoken-content">Spoken content</Label>
        <Textarea id="voice-spoken-content" rows={3} value={voice.spoken_content} disabled={disabled}
          onChange={(event) => update({ spoken_content: event.target.value })}
          aria-describedby="voice-content-help" />
        <p id="voice-content-help" className="text-xs text-muted-foreground">
          Optional spoken equivalent for media or visual display steps. Images, videos, PDFs, and radar displays without spoken content are skipped and marked complete in Voice Mode.
        </p>
      </div>
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
      <div className="space-y-1 rounded-lg bg-white/60 p-3" aria-label="Voice presentation preview">
        <p className="text-xs font-semibold">Spoken preview</p>
        {preview.skipped ? (
          <p className="text-xs text-amber-800">This visual step will be skipped and marked complete because it has no spoken content.</p>
        ) : (
          <>
            <p className="text-xs">Initial speech: {preview.prompt}</p>
            {isChoice && preview.choices.length ? <p className="text-xs">{voice.option_presentation === 'examples' ? 'Examples' : 'Choices'}: {preview.choices.join('; ')}</p> : null}
            {preview.spokenContent ? <p className="text-xs">Content: {preview.spokenContent}</p> : null}
          </>
        )}
        {isChoice && preview.canReadAll ? <p className="text-xs">“Read all the options” is available.</p> : null}
      </div>
      <Button type="button" variant="ghost" disabled={disabled || !metadata?.voice}
        onClick={() => onChange(resetVoicePresentation(metadata))}>Reset voice presentation</Button>
    </section>
  );
};
